import { useState } from 'react';
import { useCanAccess } from '../../../hooks/useAccess';
import { WELLNESS_CATEGORIES } from '../../../utils/sportsData';
import PaywallBanner from '../shared/PaywallBanner';
import { ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';

export default function WellnessScreen() {
  const canPro = useCanAccess('nutrition_logging');
  const canElite = useCanAccess('ai_meal_plans');
  const [breathState, setBreathState] = useState('idle');
  const [breathCount, setBreathCount] = useState(0);
  const [meditationSeconds, setMeditationSeconds] = useState(0);
  const [meditating, setMeditating] = useState(false);

  function startBreathing() {
    setBreathState('inhale');
    setBreathCount((c) => c + 1);
    setTimeout(() => setBreathState('hold'), 4000);
    setTimeout(() => setBreathState('exhale'), 8000);
    setTimeout(() => setBreathState('idle'), 16000);
  }

  const breathColors = { idle: 'rgba(255,255,255,0.1)', inhale: 'rgba(232,255,71,0.3)', hold: 'rgba(0,212,255,0.3)', exhale: 'rgba(255,107,53,0.2)' };
  const breathLabels = { idle: 'Tap to breathe', inhale: 'Inhale... (4s)', hold: 'Hold... (4s)', exhale: 'Exhale... (8s)' };

  function toggleMeditation() {
    if (meditating) {
      setMeditating(false);
      return;
    }
    setMeditating(true);
    setMeditationSeconds(300);
  }

  return (
    <div style={{ padding: '20px 16px 0' }}>
      <div style={{ fontFamily: 'Bebas Neue', fontSize: 30, marginBottom: 4 }}>WELLNESS</div>
      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 16 }}>Recovery, yoga, mindset & sleep</div>

      <div className="fit-card" style={{ marginBottom: 14 }}>
        <div className="fit-section-label">BOX BREATHING · STRESS RESET</div>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
          <motion.div
            onClick={startBreathing}
            animate={{ scale: breathState === 'inhale' ? 1.25 : breathState === 'exhale' ? 0.85 : 1 }}
            transition={{ duration: 1 }}
            style={{
              width: 100, height: 100, borderRadius: '50%',
              background: breathColors[breathState],
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
              border: `2px solid ${breathState === 'idle' ? 'rgba(255,255,255,0.1)' : 'rgba(232,255,71,0.4)'}`,
            }}
          >
            <div style={{ fontSize: 11, textAlign: 'center', color: breathState === 'idle' ? 'rgba(255,255,255,0.5)' : '#fff', fontWeight: 500, lineHeight: 1.4 }}>
              {breathLabels[breathState]}
            </div>
          </motion.div>
        </div>
        {breathCount > 0 && <div style={{ textAlign: 'center', fontSize: 10, color: 'rgba(255,255,255,0.3)' }}>{breathCount} breath cycles completed</div>}
      </div>

      <div className="fit-card" style={{ marginBottom: 14 }}>
        <div className="fit-section-label">MEDITATION TIMER</div>
        <div style={{ textAlign: 'center', marginBottom: 12 }}>
          <div style={{ fontFamily: 'Bebas Neue', fontSize: 48, color: '#00D4FF' }}>
            {Math.floor(meditationSeconds / 60)}:{String(meditationSeconds % 60).padStart(2, '0')}
          </div>
        </div>
        <button type="button" onClick={toggleMeditation} className="fit-btn-primary">
          {meditating ? 'Pause' : 'Start 5-min Session'}
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {WELLNESS_CATEGORIES.map((cat) => {
          const locked = (cat.tier === 'pro' && !canPro) || (cat.tier === 'elite' && !canElite);
          const el = (
            <div className="fit-card-sm" style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(0,212,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>{cat.emoji}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{cat.label}</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>{cat.desc}</div>
              </div>
              {cat.tier !== 'free' ? (
                <span className={`fit-tag ${cat.tier === 'elite' ? 'fit-tag-elite' : 'fit-tag-pro'}`}>{cat.tag}</span>
              ) : (
                <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.2)' }}>{cat.tag}</span>
              )}
              <ChevronRight size={16} color="rgba(255,255,255,0.2)" />
            </div>
          );
          return locked ? (
            <PaywallBanner key={cat.id} feature={cat.label} requiredTier={cat.tier}>{el}</PaywallBanner>
          ) : (
            <div key={cat.id}>{el}</div>
          );
        })}
      </div>
    </div>
  );
}
